import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Details, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { useGetHrPsychologicalSafetyOverviewQuery } from '../../../hooks/queries/hr/useGetHrPsychologicalSafetyOverviewQuery';
import { useGetOrganizationOngoingSurveysCountQuery } from '../../../hooks/queries/useGetOrganizationOngoingSurveysCountQuery';
import { useGetOrganizationPlannedSurveysCountQuery } from '../../../hooks/queries/useGetOrganizationPlannedSurveysCountQuery';
import { useGetUserinfo } from '../../../hooks/queries/useGetUserinfo';
import { UserRoles } from '../../../api/queries';
import { SummaryCard } from './components/SummaryCard';
import { ActionCards } from './components/ActionCards';
import { TeamDistribution } from './components/TeamDistribution';
import { AreaList } from './components/AreaList';
import { TeamsSection } from './components/TeamsSection';
import {
  statusDistribution,
  TeamFilter,
  teamStatus,
  teamsNeedingFollowUp,
  teamsWithResults,
  weakestFirst,
} from './hrPsychologicalSafetyView';
import classes from './HRPsychologicalSafety.module.css';

const FAQ = ['whatIsShown', 'howToUse', 'anonymity'] as const;

export const HRPsychologicalSafety = (): ReactElement => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetHrPsychologicalSafetyOverviewQuery();
  const { data: ongoing } = useGetOrganizationOngoingSurveysCountQuery();
  const { data: planned } = useGetOrganizationPlannedSurveysCountQuery();
  const { data: userInfo } = useGetUserinfo();
  const [filter, setFilter] = useState<TeamFilter>('results');

  const showTeams = (next: TeamFilter) => {
    setFilter(next);
    document.getElementById('hr-ps-teams')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const content = () => {
    if (isLoading) {
      return (
        <div aria-busy="true" className={classes.loading}>
          <span className="ds-sr-only">{t('hrPsychologicalSafety.loading')}</span>
          <Skeleton variant="rectangle" width="100%" height={260} />
          <Skeleton variant="rectangle" width="100%" height={220} />
        </div>
      );
    }

    if (error || !data) {
      return (
        <Alert data-color="danger">
          <Heading level={2} data-size="2xs">
            {t('hrPsychologicalSafety.error.title')}
          </Heading>
          <Paragraph>{t('hrPsychologicalSafety.error.message')}</Paragraph>
        </Alert>
      );
    }

    const withResults = teamsWithResults(data.teams);
    const followUp = teamsNeedingFollowUp(data.teams);
    const missing = data.alerts.missingMeasurements;
    const challenging = withResults.filter((team) => teamStatus(team) === 'danger');
    const weakestCategory = weakestFirst(data.organizationalInsights.categories)[0];

    return (
      <>
        <SummaryCard
          averageScore={data.crossTeamInsights.averageScore}
          teamsWithResults={withResults.length}
          activeTeams={data.teams.length}
          ongoingSurveys={ongoing?.count}
          plannedSurveys={planned?.count}
        />
        <ActionCards
          challengingTeams={challenging}
          weakestCategory={weakestCategory}
          missingTeams={missing}
          onShowTeams={showTeams}
        />
        <TeamDistribution
          counts={statusDistribution(data.teams)}
          total={data.teams.length}
          averageScore={data.crossTeamInsights.averageScore}
          onShowTeams={showTeams}
        />
        {data.organizationalInsights.categories.length > 0 && (
          <AreaList
            categories={data.organizationalInsights.categories}
            questions={data.organizationalInsights.questions}
            teamsWithResults={withResults.length}
          />
        )}
        <TeamsSection
          filter={filter}
          onFilterChange={setFilter}
          withResults={withResults}
          followUp={followUp}
          missing={missing}
          canPlan={Boolean(userInfo?.userRoles?.includes(UserRoles.ORG_ADMIN))}
        />
        <section aria-labelledby="hr-ps-faq" className={classes.faq}>
          <Heading level={2} data-size="sm" id="hr-ps-faq">
            {t('hrPsychologicalSafety.faq.title')}
          </Heading>
          {FAQ.map((key) => (
            <Details key={key}>
              <Details.Summary>{t(`hrPsychologicalSafety.faq.${key}.question`)}</Details.Summary>
              <Details.Content>{t(`hrPsychologicalSafety.faq.${key}.answer`)}</Details.Content>
            </Details>
          ))}
        </section>
      </>
    );
  };

  return (
    <div className={classes.page}>
      <div aria-hidden="true" className={classes.backdrop} />
      <div className={classes.content}>
        <div className={classes.heading}>
          <span className={classes.eyebrow}>{t('hrPsychologicalSafety.eyebrow')}</span>
          <Heading level={1} data-size="xl">
            {t('hrPsychologicalSafety.title')}
          </Heading>
          <Paragraph className={classes.subtle}>{t('hrPsychologicalSafety.intro')}</Paragraph>
        </div>
        {content()}
      </div>
    </div>
  );
};
